import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import GridBox from '@components/atoms/GridBox';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import { Heading1, Heading2, Paragraph } from '@components/atoms/Typography';
import GridLinkList from '@components/molecules/list/GridLinkList';
import PageContainer from '@components/molecules/PageContainer';
import SearchForm from '@components/organisms/forms/SearchForm';
import BREAKPOINTS from '@lib/constants/breakpoints';
import env from '@lib/constants/env';
import ROUTES from '@lib/constants/routes';
import airtableBases, { ClubData } from '@lib/helpers/airtableBases';
import { groupBy } from '@lib/helpers/misc';
import { STATES } from '@shared/constants';
import { GetStaticProps } from 'next';
import { BreadcrumbJsonLd, SiteLinksSearchBoxJsonLd } from 'next-seo';
import { useAmp } from 'next/amp';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

const Header = styled.header`
  position: relative;
  padding-top: 80px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 50px;
    text-align: center;
  }
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;

  > div {
    padding: 32px;
  }

  > div:first-child {
    max-width: 600px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;

    > div:nth-child(2) {
      display: none;
    }
    > div:first-child {
      max-width: none;
    }
  }
`;

const SearchWrapper = styled(GridBox)`
  margin: 16px 0 0;
  justify-content: flex-start;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    justify-content: center;
  }
`;

interface Props {
  clubs: ClubData[];
  uf: string;
}

const MotoClubStates = ({ clubs, uf }: Props) => {
  const isAmp = useAmp();
  const router = useRouter();

  if (!uf) return <PageContainer loading />;

  const stateName = STATES[uf.toUpperCase() as keyof typeof STATES];

  const groupedByCity = groupBy(
    clubs
      .filter(
        (club, index, self) =>
          index === self.findIndex((c) => c.Motoclube === club.Motoclube),
      )
      .sort((a, b) => a.Motoclube?.localeCompare(b.Motoclube))
      .sort((a, b) => a.Cidade?.localeCompare(b?.Cidade || 'zzzzzzz') || 1),
    (c) => c.Cidade,
  );

  return (
    <PageContainer
      title={`Moto clubes de ${stateName} | Luna`}
      metaDescription={`Confira os principais motoclubes, moto grupos e confrarias motociclistas em ${stateName}, procure um clube de moto na sua cidade!`}
    >
      <SiteLinksSearchBoxJsonLd
        url={`${env.baseUrl}${ROUTES.stateClub(uf)}`}
        potentialActions={[
          {
            target: `${env.baseUrl}${ROUTES.clubs}/busca?q`,
            queryInput: 'term',
          },
        ]}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: 'Moto Clubes',
            item: `${env.baseUrl}${ROUTES.clubs}`,
          },
          {
            position: 3,
            name: `Moto clubes em ${stateName}`,
            item: `${env.baseUrl}${ROUTES.stateClub(uf)}`,
          },
        ]}
      />
      <Header>
        <HeaderGrid>
          <div>
            <Heading1>
              Moto clubes em
              {` ${stateName}`}
            </Heading1>
            <Spacing top={2} bottom={4}>
              <Paragraph>
                Veja os motoclubes em
                {` ${stateName}`}
              </Paragraph>
            </Spacing>
            {isAmp ? (
              <a href="#clubes">
                <PrimaryButton isBlock>Ver Motoclubes</PrimaryButton>
              </a>
            ) : (
              <SearchWrapper justifyContent="flex-end">
                <SearchForm
                  placeholder="Buscar Grupos"
                  onSearch={(term) => {
                    router.push(`${ROUTES.clubs}/busca?q=${term}`);
                  }}
                />
              </SearchWrapper>
            )}
          </div>
          {!isAmp && (
            <div style={{ position: 'relative', height: 600 }}>
              <Image
                alt="Grupo andando de moto"
                src="https://images.unsplash.com/photo-1558980664-2cd663cf8dde?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80"
                layout="fill"
                objectFit="cover"
              />
            </div>
          )}
        </HeaderGrid>
      </Header>
      <Section id="clubes" pastel>
        <Heading2 center>
          Moto clubes de
          {` ${stateName}`}
        </Heading2>
        <GridLinkList
          links={[...groupedByCity.values()].flat().map((club) => ({
            label: club.Motoclube + (club.Cidade ? ` - ${club.Cidade}` : ''),
            path: ROUTES.club(uf, club.Motoclube),
          }))}
        />
      </Section>

      <Section>
        <Heading2 center>Quer adicionar seu moto clube?</Heading2>
        <Spacing top={2}>
          <Paragraph center>
            Caso participe de um moto clube, você pode adicionar seu grupo para
            que outros motociclistas o encontrem!
          </Paragraph>
        </Spacing>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://airtable.com/shr4RSpZLUYF8Z0oW"
        >
          <PrimaryButton style={{ margin: '32px auto' }}>
            Adicionar Clube
          </PrimaryButton>
        </a>
      </Section>
    </PageContainer>
  );
};

export async function getStaticPaths() {
  return {
    paths: ['/moto-clubes/sp'],
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const uf = params?.uf?.toString().toLowerCase().trim() as string;

  const clubs = await airtableBases.getMotoClubsByUF(uf);

  return {
    props: { clubs, uf },
    revalidate: 3600,
  };
};

export default MotoClubStates;
